import { getAPI, postAPI, putAPI } from 'services/api/apiAsync';
import { AppDispatch, AppState } from 'app/store';
import {
  API_ENDPOINTS,
  COMPANION_PLAN_CODE,
  PANGO_BILLING_AUTHORIZATION_TOKEN,
} from 'utils/constants';
import { getFromBrandConfig } from 'brand-config';
import type { LooseObjectType } from 'utils/globalTypes';
import { isBoolean } from 'utils';
import { selectBrandCode } from 'services/brand/brandSelectors';
import {
  fetchOfferDetailsByCode,
  getOfferDetails,
} from 'services/offer/offerAsync';
import { fetchUserDetails } from 'services/user/userAsync';
import {
  selectUserId,
  selectUserName,
  selectUserEmail,
  selectUserAddress,
} from 'services/user/userSelectors';
import { clearVendorEmployeeId } from 'services/user/userSlice';
import {
  setAvailableCtpPlan,
  setConversionEligibility,
  setEligibility,
  setBillingAccountInfo,
  setAvailableOffers,
  setIsAvailableOffersFetched,
  setSubscriptionInfo,
} from './ctpSlice';
import {
  selectBillingAccountDetails,
  selectIsDiscoverEmployee,
  selectSubscriptionDetails,
} from './ctpSelectors';
import { getAvailableCtpPlan } from './utils';
import {
  CreateZuoraAccountParams,
  IAdditionalSubscription,
  ISubscriptionInfo,
} from './types';

/* The bundle's second subscription. Mapped defensively — the field is absent for
   every member outside the Privacy Assist bundle, and the BE has been seen to
   use both casings on this response (see `currentPeriodEndsAt` below). */
function mapAdditionalSubscriptions(
  response: LooseObjectType,
): IAdditionalSubscription[] {
  const raw = (response.additionalSubscriptions ??
    response.additional_subscriptions) as LooseObjectType[] | undefined;
  if (!Array.isArray(raw)) return [];

  return raw.map((sub) => {
    const plan = sub.plan as LooseObjectType | undefined;
    return {
      planCode: (sub.planCode as string) ?? (sub.plan_code as string) ?? '',
      name: (sub.name as string) ?? '',
      termEnd: (sub.termEnd as string) ?? (sub.term_end as string) ?? '',
      plan: { title: (plan?.title as string) ?? '' },
    };
  });
}

function mapSubscriptionResponse(response: LooseObjectType): ISubscriptionInfo {
  const card = response.creditCard as LooseObjectType | undefined;
  return {
    additionalSubscriptions: mapAdditionalSubscriptions(response),
    creditBalance:
      Number(response.creditBalance ?? response.credit_balance ?? 0) || 0,
    status: (response.status as string) ?? '',
    createdAt: (response.createdAt as string) ?? '',
    cancelledAt: (response.cancelledAt as string | null) ?? null,
    currentPeriodEndsAt:
      (response.currentPeriodEndsAt as string) ??
      (response.current_period_ends_at as string) ??
      '',
    creditCard: {
      lastFour: (card?.lastFour as string) ?? (card?.last_four as string) ?? '',
      type: (card?.type as string) ?? '',
      expirationMonth:
        (card?.expirationMonth as string | number | undefined) ??
        (card?.expiration_month as string | number | undefined) ??
        null,
      expirationYear:
        (card?.expirationYear as string | number | undefined) ??
        (card?.expiration_year as string | number | undefined) ??
        null,
    },
  };
}

/** Allow backend to persist user, offer, and subscription after marketing-offer change. */
const POST_OFFER_CHANGE_REFRESH_DELAY_MS = 2000;

const delay = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

export function fetchAvailableOffers() {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: API_ENDPOINTS.getAvailableOffers,
      };
      const response = await dispatch(getAPI(config)).unwrap();
      dispatch(setAvailableOffers(response));
      dispatch(setIsAvailableOffersFetched(true));
      if (Array.isArray(response)) {
        dispatch(setAvailableCtpPlan(getAvailableCtpPlan(response)));
      }
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function fetchRSATokens() {
  return async (dispatch: AppDispatch, getState: AppState) => {
    try {
      const store = getState();
      const brandCode = selectBrandCode(store);
      const config = {
        url: API_ENDPOINTS.zuoraRSATokens,
        headers: {
          Authorization: PANGO_BILLING_AUTHORIZATION_TOKEN,
        },
        data: {
          source: 'web',
          brandCode,
        },
      };
      const response = await dispatch(postAPI(config)).unwrap();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function fetchConversionEligibility() {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: API_ENDPOINTS.conversionEligibility,
      };
      const response = await dispatch(getAPI(config)).unwrap();
      if (response) {
        dispatch(setConversionEligibility(response));
      }
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function fetchEligibility() {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: API_ENDPOINTS.eligibility,
      };
      const response = await dispatch(getAPI(config)).unwrap();
      if (response) {
        dispatch(setEligibility(response));
      }
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function fetchPangoBillingOfferDetails(offerCode: string) {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: API_ENDPOINTS.getPangoBillingOfferDetails,
        data: {
          offer_code: offerCode,
        },
      };
      const response = await dispatch(postAPI(config)).unwrap();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function fetchBillingInvoices(subscriptionId: string, pageNum: number) {
  return async (dispatch: AppDispatch, getState: AppState) => {
    try {
      /* Angular parity — `my-account.service.ts:279`. Discover-
       * employee users hit the proprietary invoice endpoint (which
       * ignores subscriptionId); every other brand hits the Zuora
       * subscription-invoices route. */
      const isDiscoverEmployee = selectIsDiscoverEmployee(getState());
      const url = isDiscoverEmployee
        ? `${API_ENDPOINTS.getDiscoverBillingInvoices}?pageNum=${pageNum}`
        : `${API_ENDPOINTS.getSubscriptions}/${subscriptionId}/invoices?pageNum=${pageNum}`;
      const response = await dispatch(getAPI({ url })).unwrap();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function acceptLegalPolicies(data: string[]) {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: API_ENDPOINTS.acceptLegalPolicies,
        data,
      };
      const response = await dispatch(postAPI(config)).unwrap();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function changeMarketingOffer(offerCode: string) {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: `${API_ENDPOINTS.changeMarketingOffer}/${offerCode}`,
        data: {},
      };
      const response = await dispatch(putAPI(config)).unwrap();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/* Function to fetch the user billing account details.
 *
 * `accountFound` is a client-derived flag (not a BE field) — mirrors
 * Angular's `my-account.service.ts:465-484 getBillingAccountInfoById`:
 *   – 2xx response → `accountFound: true`
 *   – 404 response → `accountFound: false` (this is the Discover-
 *     employee case where no Zuora account exists yet)
 *   – any other error → left unset so consumers stay conservative.
 * `selectIsDiscoverProprietaryUpdateNeeded` gates specifically on
 * `accountFound === false`, so nothing fires on `undefined`. */
export function fetchBillingAccountInfo() {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: API_ENDPOINTS.getBillingAccountInfo,
      };
      const response = await dispatch(getAPI(config)).unwrap();
      dispatch(
        setBillingAccountInfo({
          defaultPaymentMethodId: response?.defaultPaymentMethodId ?? null,
          autoRenew: response?.autoRenew ?? null,
          accountFound: true,
        }),
      );
      return response;
    } catch (error) {
      /* This endpoint is on the JSON-stringify branch of apiAsync's
       * interceptor (see the comment there), so `error` is a
       * `{status, data}` payload as a stringified JSON. Parse it to
       * detect the 404 that means "no Zuora account yet". */
      let status: number | undefined;
      try {
        const parsed = JSON.parse(error as string) as { status?: number };
        status = parsed?.status;
      } catch {
        status = (error as { status?: number } | undefined)?.status;
      }
      const isNotFound = status === 404;
      dispatch(
        setBillingAccountInfo({
          defaultPaymentMethodId: null,
          autoRenew: null,
          accountFound: isNotFound ? false : undefined,
        }),
      );
      return Promise.reject(error);
    }
  };
}

/**
 * Angular parity — `my-account.service.ts:359-373 patchZuoraBillingInfo`.
 * Swaps the credit card on the user's existing Zuora account.
 * POSTs the new payment-method token returned by the Zuora HPM
 * iframe; nothing else needs to change on the account. Used by the
 * dashboard Card Expiry Reminder modal (standard variant).
 */
export function updatePaymentMethodToken(paymentMethodToken: string) {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: API_ENDPOINTS.updateBillingAccountInfo,
        data: { payment_method_token: paymentMethodToken },
      };
      await dispatch(postAPI(config)).unwrap();
      const response = await dispatch(fetchBillingAccountInfo());
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/**
 * Function to update the user billing account details
 */
export function updateBillingAccountInfo(offerCode: string) {
  return async (dispatch: AppDispatch, getState: AppState) => {
    try {
      const store = getState();
      const userId = selectUserId(store);
      const config = {
        url: API_ENDPOINTS.updateBillingAccountInfo,
        data: {
          user_id: userId,
          auto_renew: true,
          offer_code: offerCode,
        },
      };
      await dispatch(postAPI(config)).unwrap();
      await dispatch(acceptLegalPolicies(['billing', 'refund']));
      const response = await dispatch(fetchBillingAccountInfo());
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function createZuoraAccount({
  token,
  offerCode,
  autoRenew,
}: CreateZuoraAccountParams) {
  return async (dispatch: AppDispatch, getState: AppState) => {
    try {
      const store = getState();
      const userId = selectUserId(store);
      const userName = selectUserName(store);
      const userEmail = selectUserEmail(store);
      const userAddress = selectUserAddress(store);
      const brandCode = selectBrandCode(store);
      let offerDetails: LooseObjectType = {};

      if (offerCode) {
        offerDetails = await dispatch(fetchPangoBillingOfferDetails(offerCode));
      }

      const config = {
        url: API_ENDPOINTS.createZuoraAccount,
        data: {
          billing_contact: {
            address1: userAddress.street1,
            city: userAddress.city,
            country: userAddress.country,
            state: userAddress.state,
            zip_code: userAddress.zip,
            first_name: userName.first,
            last_name: userName.last,
          },
          brand_code: brandCode,
          category_key:
            offerDetails?.category_key ||
            getFromBrandConfig('defaultBillingCategoryKey'),
          currency: 'USD',
          email: userEmail,
          payment_method_token: token,
          user_id: userId,
          auto_renew: isBoolean(autoRenew) ? autoRenew : null,
          offer_code: offerCode || null,
        },
      };
      const response = await dispatch(postAPI(config)).unwrap();
      await new Promise((resolve) => {
        setTimeout(resolve, 1000);
      });
      /* Refresh billing account AND subscription — the Payment
       * Information card reads `subscriptionDetails.creditCard` and
       * would otherwise stay stale until the next login. Fired in
       * parallel — neither depends on the other. */
      await Promise.allSettled([
        dispatch(fetchBillingAccountInfo()),
        // eslint-disable-next-line no-use-before-define
        dispatch(fetchSubscriptionInfo()),
      ]);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/**
 * Angular parity — `billing-details.component.ts:204-234
 * zuoraCallbackSubject`. Shared save-payment branch used whenever a
 * user submits a new Zuora HPM token:
 *   – Discover-employee (proprietaryFlag) → create a fresh Zuora
 *     account and locally clear `user.vendorEmployeeId` so the
 *     proprietary variant / banner doesn't re-fire in the same
 *     session.
 *   – Everyone else → PATCH the token onto the existing Zuora
 *     account.
 *
 * Every consumer that saves a payment method (Card Expiry Reminder
 * modal, future billing-details edit) MUST dispatch this rather than
 * calling `updatePaymentMethodToken` / `createZuoraAccount`
 * directly, so the proprietary path stays wired for every entry
 * point.
 */
export function saveNewPaymentMethod(
  paymentMethodToken: string,
  offerCode?: string,
) {
  return async (dispatch: AppDispatch, getState: AppState) => {
    const isProprietary = selectIsDiscoverEmployee(getState());
    if (isProprietary) {
      /* Clear `vendorEmployeeId` BEFORE `createZuoraAccount` — its
       * internal subscription refresh branches on
       * `selectIsDiscoverEmployee`, and if the flag is still set the
       * refresh hits `/discover/subscription` (proprietary, stale)
       * instead of `/subscribers/` (Zuora, fresh new card). Angular's
       * `billing-details.component.ts:209` dispatches
       * `USER_REMOVE_VENDORID` first, then calls
       * `getSubscriptionDetailsAndStore`. */
      dispatch(clearVendorEmployeeId());
      const response = await dispatch(
        createZuoraAccount({ token: paymentMethodToken, offerCode }),
      );
      return response;
    }
    return dispatch(updatePaymentMethodToken(paymentMethodToken));
  };
}

export function fetchCurrentPeriodEndsAt() {
  return async (dispatch: AppDispatch, getState: AppState) => {
    try {
      const userId = selectUserId(getState());
      if (!userId) {
        return null;
      }
      const config = {
        url: `${API_ENDPOINTS.userProfile}/${userId}/current_period_ends_at`,
      };
      const response = (await dispatch(
        getAPI(config),
      ).unwrap()) as LooseObjectType;
      const currentPeriodEndsAt =
        (response.currentPeriodEndsAt as string) ??
        (response.current_period_ends_at as string) ??
        null;
      if (!currentPeriodEndsAt) {
        return null;
      }

      const existing = getState().ctp.subscriptionInfo;
      dispatch(
        setSubscriptionInfo({
          status: existing?.status ?? '',
          createdAt: existing?.createdAt ?? '',
          cancelledAt: existing?.cancelledAt ?? null,
          currentPeriodEndsAt,
          creditBalance: existing?.creditBalance ?? 0,
          creditCard: existing?.creditCard ?? { lastFour: '', type: '' },
          /* This endpoint returns only the period end date, so carry whatever
             the subscription fetch already put in the slice. The object is
             rebuilt rather than merged, so omitting this wipes the companion on
             every period-end refresh. */
          additionalSubscriptions: existing?.additionalSubscriptions ?? [],
        }),
      );
      return currentPeriodEndsAt;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

export function fetchSubscriptionInfo() {
  return async (dispatch: AppDispatch, getState: AppState) => {
    try {
      /* Angular parity — `my-account.service.ts:215`. Discover-
       * employee users pull subscription details from the
       * proprietary endpoint; every other brand hits the standard
       * subscribers route. */
      const isDiscoverEmployee = selectIsDiscoverEmployee(getState());
      const config = {
        url: isDiscoverEmployee
          ? API_ENDPOINTS.getDiscoverSubscriptionInfo
          : API_ENDPOINTS.getSubscriptionInfo,
      };
      const response = (await dispatch(
        getAPI(config),
      ).unwrap()) as LooseObjectType;
      if (response) {
        const mapped = mapSubscriptionResponse(response);
        dispatch(setSubscriptionInfo(mapped));
        if (!mapped.currentPeriodEndsAt) {
          await dispatch(fetchCurrentPeriodEndsAt());
        }
      }
      return response;
    } catch (error) {
      const existing = getState().ctp.subscriptionInfo;
      if (!existing?.currentPeriodEndsAt) {
        try {
          const periodEndsAt = await dispatch(fetchCurrentPeriodEndsAt());
          if (periodEndsAt) {
            return { currentPeriodEndsAt: periodEndsAt };
          }
        } catch {
          // fall through to reject original error
        }
      }
      return Promise.reject(error);
    }
  };
}

export function convertToBillableOffer({
  token,
  offerCode,
  planCode,
}: {
  token?: string;
  offerCode: string;
  /** Target plan code (Offers & Upgrades); omit for Keep My Plan on current plan. */
  planCode?: string;
}) {
  return async (dispatch: AppDispatch, getState: AppState) => {
    try {
      if (token) {
        await dispatch(createZuoraAccount({ token, offerCode }));
      }
      await dispatch(acceptLegalPolicies(['billing', 'refund']));
      const response = await dispatch(changeMarketingOffer(offerCode));

      await Promise.allSettled([
        dispatch(fetchConversionEligibility()),
        dispatch(fetchAvailableOffers()),
      ]);

      await delay(POST_OFFER_CHANGE_REFRESH_DELAY_MS);

      const billingAccount = selectBillingAccountDetails(getState());
      const delayedRefreshTasks: Promise<unknown>[] = [
        dispatch(fetchUserDetails()),
        planCode
          ? dispatch(fetchOfferDetailsByCode(offerCode, planCode))
          : dispatch(getOfferDetails()),
        dispatch(fetchCurrentPeriodEndsAt()),
      ];
      if (billingAccount?.defaultPaymentMethodId) {
        delayedRefreshTasks.push(dispatch(fetchSubscriptionInfo()));
      }
      await Promise.allSettled(delayedRefreshTasks);

      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/* Update the Zoura payment details and fetch the latest payment information details from Zuora */
export function updateZuoraPaymentInformationDetails(token: string) {
  return async (dispatch: AppDispatch, getState: AppState) => {
    try {
      const store = getState();
      const userId = selectUserId(store);
      const config = {
        url: API_ENDPOINTS.updateZuoraAccount,
        data: {
          payment_method_token: token,
          user_id: userId,
        },
      };
      const response = await dispatch(postAPI(config)).unwrap();
      /* Give the BE a moment to propagate the new card to both the
       * billing-account and subscription tables. Same 1-second delay
       * the Discover proprietary sibling (`createZuoraAccount`) has
       * used since day one — refetching immediately can return stale
       * data (subscription without the new card, billing account
       * without the new payment method id). */
      await new Promise((resolve) => {
        setTimeout(resolve, 2000);
      });
      /* Refresh both subscription AND billing account after the
       * save. Standard save previously refreshed only subscription,
       * which meant `billingAccountInfo.defaultPaymentMethodId`
       * stayed stale on the client and the paid-flow gate
       * (`selectHasZuoraPaymentMethod`) never flipped. Fired in
       * parallel — neither depends on the other. */
      await Promise.allSettled([
        dispatch(fetchSubscriptionInfo()),
        dispatch(fetchBillingAccountInfo()),
      ]);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/**
 * Function to capture the credit card details of the breach user
 */
export function captureCreditCardDetails({
  token,
  offerCode,
  autoRenew,
}: CreateZuoraAccountParams) {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await dispatch(
        createZuoraAccount({ token, offerCode, autoRenew }),
      );
      if (offerCode && autoRenew) {
        await dispatch(acceptLegalPolicies(['billing', 'refund']));
      }
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/**
 * Cancels the Card Theft Protection companion when the member leaves the
 * Privacy Assist bundle.
 *
 * Angular parity — `change-plan-modal.component.ts:266-274`. After a successful
 * plan change it walks `additionalSubscriptions` and cancels **only** the
 * entries whose `planCode` is `crdtftprt`, by `name` alone; its
 * `cancelPlan(user_id, name)` takes a user id and discards it
 * (`my-account.service.ts:166-171`), so only the name is sent here.
 *
 * Card Theft Protection bills as its own Zuora subscription and the plan change
 * does not touch it, so leaving the bundle has to stop it separately. Any other
 * `additionalSubscriptions` entry is left alone — the filter below is the whole
 * of the decision about what gets cancelled.
 *
 * `wasBundlePlan` is passed in because the caller has to capture it **before**
 * the change: afterwards the offer slice describes the new plan. Angular reads
 * it after and only works because its refresh calls have not resolved yet
 * (`:254-256`); that ordering accident is not reproduced.
 *
 * A no-op, with no network call, for everyone else.
 */
export function cancelCompanionSubscriptions(wasBundlePlan: boolean) {
  return async (dispatch: AppDispatch, getState: AppState) => {
    if (!wasBundlePlan) {
      return null;
    }

    const companions = (
      selectSubscriptionDetails(getState())?.additionalSubscriptions ?? []
    ).filter((sub) => sub.planCode === COMPANION_PLAN_CODE);

    if (!companions.length) {
      return null;
    }

    /* Angular cancels each match rather than only the first, so the same shape
       is kept even though the bundle ships exactly one companion today. */
    return Promise.all(
      companions.map((sub) =>
        dispatch(
          postAPI({
            url: API_ENDPOINTS.cancelSubscription,
            data: { name: sub.name },
          }),
        ).unwrap(),
      ),
    );
  };
}
